
import { BookOpen, LayoutDashboard, Settings, Timer, Users, Building2 } from "lucide-react";
import { PERMISSIONS } from "@src/shared/constants/permissions";
import type { Permission } from "@src/shared/constants/permissions";

export interface SectionChild {
  type: "section";
  label: string;
  sectionId: string;
  showInNav?: boolean;
  isDisabled?: boolean;
  /** Single permission required to view this menu item */
  permission?: Permission | string;
  /** Array of permissions - user must have ALL to view */
  requiredPermissions?: (Permission | string)[];
  /** Array of permissions - user must have ANY ONE to view */
  anyPermissions?: (Permission | string)[];
}

export interface RouteChild {
  type: "route";
  label: string;
  route: string;
  showInNav?: boolean;
  isDisabled?: boolean;
  /** Single permission required to view this menu item */
  permission?: Permission | string;
  /** Array of permissions - user must have ALL to view */
  requiredPermissions?: (Permission | string)[];
  /** Array of permissions - user must have ANY ONE to view */
  anyPermissions?: (Permission | string)[];
}

export type SidebarChild = SectionChild | RouteChild;

export interface SidebarMenuItem {
  label: string;
  icon: React.ReactNode;
  route?: string;
  children?: SidebarChild[];
  showInNav?: boolean;
  isDisabled?: boolean;
  /** Single permission required to view this menu item */
  permission?: Permission | string;
  /** Array of permissions - user must have ALL to view */
  requiredPermissions?: (Permission | string)[];
  /** Array of permissions - user must have ANY ONE to view */
  anyPermissions?: (Permission | string)[];
}

export const sidebarMenuItems: SidebarMenuItem[] = [
  {
    label: "Dashboard",
    icon: <LayoutDashboard size={16} />,
    showInNav: true,
    route: "/dashboard",
    isDisabled: false,
    // No permission required - accessible to all authenticated users
    // Components inside handle their own permission checks
  },

  {
    label: "Pascos",
    icon: <BookOpen size={16} />,
    showInNav: true,
    anyPermissions: [PERMISSIONS.PASCO_READ, PERMISSIONS.PASCO_UPLOAD, PERMISSIONS.PASCO_EDIT],
    children: [
      {
        type: "route",
        label: "All Pascos",
        route: "/pascos",
        permission: PERMISSIONS.PASCO_READ,
      },
      {
        type: "route",
        label: "Create Pasco",
        route: "/pascos/new",
        permission: PERMISSIONS.PASCO_UPLOAD,
      },
      {
        type: 'route',
        label: 'Pending Approval',
        route: '/pascos/pending',
        permission: PERMISSIONS.PASCO_APPROVE,
      },
      {
        type: 'route',
        label: 'Pending Review',
        route: '/pascos/review',
        permission: PERMISSIONS.PASCO_EDIT,
      },
      {
        type: "route",
        label: 'Processed',
        route: '/pascos/processed',
        permission: PERMISSIONS.PASCO_READ,
      }
    ]
  },

  {
    label: 'Users & Access',
    icon: <Users size={16} />,
    showInNav: true,
    isDisabled: false,
    anyPermissions: [PERMISSIONS.USER_READ, PERMISSIONS.ROLE_READ, PERMISSIONS.PERMISSION_READ],
    children: [
      {
        type: 'route',
        label: 'All Users',
        route: '/users',
        permission: PERMISSIONS.USER_READ,
      },
      {
        type: 'route',
        label: 'Create User',
        route: '/users/new',
        permission: PERMISSIONS.USER_CREATE,
      },
      {
        type: 'route',
        label: 'Roles',
        route: '/rbac/roles',
        permission: PERMISSIONS.ROLE_READ,
      },
      {
        type: 'route',
        label: 'Permissions',
        route: '/rbac/permissions',
        permission: PERMISSIONS.PERMISSION_READ,
      }
    ]
  },

  {
    label: 'Universities',
    icon: <Building2 size={16} />,
    showInNav: true,
    permission: PERMISSIONS.UNIVERSITY_READ,
    children: [
      {
        type: 'route',
        label: 'All Universities',
        route: '/universities',
        permission: PERMISSIONS.UNIVERSITY_READ,
      },
      {
        type: 'route',
        label: 'Create University',
        route: '/universities/new',
        permission: PERMISSIONS.UNIVERSITY_CREATE,
      }
    ]
  },


  {
    label: 'Background Tasks',
    icon: <Timer size={16} />,
    showInNav: true,
    permission: PERMISSIONS.TASK_READ,
    children: [
      {
        type: 'route',
        label: 'All Tasks',
        route: '/tasks',
        permission: PERMISSIONS.TASK_READ,
      },
      {
        type: 'route',
        label: 'Failed Tasks',
        route: '/tasks/failed',
        permission: PERMISSIONS.TASK_READ,
      }
    ]
  },

  {
    label: 'System',
    icon: <Settings size={16} />,
    showInNav: true,
    isDisabled: false,
    // System health is available to all, but configurations might be restricted
    children: [
      {
        type: 'route',
        label: 'System Health',
        route: '/system/health',
        // No permission needed - available to all authenticated users
      },
      {
        type: 'route',
        label: 'Configurations',
        route: '/configurations',
        // Admin only - no specific permission, will rely on admin role
      }
    ]
  }

];
